import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  fogbugzFormulatorErrorMessage,
  fogbugzFormulatorEventSchema,
  isFogbugzFormulatorEngineErrorCode,
  type FogbugzFormulatorEngineErrorCode,
  type FogbugzFormulatorToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { fogbugzToolboxInterviewKeys } from "@/hooks/queries/use-fogbugz-toolbox-interview";
import { useClaudeRunOutcome } from "./claude-signals";
import { useToolboxBridge } from "./bridge";

/**
 * The FogBugz Formulator's half of the SpecTree Toolbox bridge, for the
 * Toolbox FogBugz Formulator page (epic PI-SPEC-SW-E309, feature
 * PI-SPEC-SW-1620).
 *
 * Interview turns run on the person's own Claude Code seat inside SpecTree
 * Toolbox (`spectreeToolbox.fogbugzFormulator`), with the linked ADO
 * project's code open; each turn reports through `onEvent`. The session is
 * server state the page reads from SpecTree; the bridge never writes there
 * except through the engine's turn posts. Review and submit go page → API.
 */

/** The fogbugz-formulator half of the bridge when the page runs inside an app that ships it, else null. */
export function useFogbugzFormulatorBridge(): FogbugzFormulatorToolsetBridge | null {
  return useToolboxBridge()?.fogbugzFormulator ?? null;
}

/**
 * What Claude is doing while a turn runs, in plain words. The engine sends no
 * activity line for this toolset, so the page cycles these. Opening turns
 * read the code first; replies check the answer against it.
 */
export const OPENING_TURN_PHRASES = [
  "Updating the product code on this computer",
  "Reading the product code",
  "Looking for the part of the product you described",
  "Thinking about the first question",
] as const;

export const REPLY_TURN_PHRASES = [
  "Reading your answer",
  "Checking it against the product code",
  "Thinking about the next question",
] as const;

/** How long each progress phrase shows before the next. */
export const PROGRESS_PHRASE_MS = 6_000;

/** The phrase to show `elapsedMs` into a turn: cycles through the list and stays on the last. Pure; exported for tests. */
export function progressPhrase(mode: "opening" | "reply", elapsedMs: number): string {
  const phrases = mode === "opening" ? OPENING_TURN_PHRASES : REPLY_TURN_PHRASES;
  const index = Math.min(Math.floor(Math.max(elapsedMs, 0) / PROGRESS_PHRASE_MS), phrases.length - 1);
  return phrases[index] ?? phrases[0];
}

/** A plain refusal, and whether Setup checks is where the person fixes it. */
export interface FogbugzFormulatorMessage {
  code: FogbugzFormulatorEngineErrorCode | "UNKNOWN";
  text: string;
  setup: boolean;
}

/** Codes the person fixes on the Setup checks page. */
const SETUP_CODES: ReadonlySet<string> = new Set([
  "CLAUDE_NOT_FOUND",
  "CLAUDE_NOT_SIGNED_IN",
  "API_KEY_BILLING",
  "PLUGIN_MISSING",
  "REFUSED",
  "REPO_NOT_SET_UP",
]);

/**
 * Plain words for a fogbugz-formulator refusal: the contract's message
 * (`fogbugzFormulatorErrorMessage`, naming the linked ADO project where it
 * matters), else the engine's own sentence. An unknown code reads as
 * "Something went wrong". Pure; exported for tests.
 */
export function fogbugzFormulatorMessage(
  code: string | undefined,
  options: { adoProject?: string; engineMessage?: string } = {},
): FogbugzFormulatorMessage {
  const trimmed = options.engineMessage?.trim();
  const fallback = trimmed === "" ? undefined : trimmed;
  if (!isFogbugzFormulatorEngineErrorCode(code)) {
    return { code: "UNKNOWN", text: fallback ?? "Something went wrong in SpecTree Toolbox. Try again.", setup: false };
  }
  return {
    code,
    text: fogbugzFormulatorErrorMessage(code, {
      ...(options.adoProject ? { adoProject: options.adoProject } : {}),
      ...(fallback ? { fallback } : {}),
    }),
    setup: SETUP_CODES.has(code),
  };
}

/** What the page knows about the session's current turn from the engine's events. */
export interface FogbugzFormulatorTurnState {
  /** The running turn's mode and start time, or null when none is known to run. */
  running: { mode: "opening" | "reply"; startedAt: number } | null;
  /** How the last turn failed while the page was open, or null. Cleared when a turn starts. */
  failure: { code: string; message: string } | null;
  /** Bumped every time a turn finishes, so the page can clear its pending answer. */
  finishedCount: number;
}

const IDLE: FogbugzFormulatorTurnState = { running: null, failure: null, finishedCount: 0 };

/**
 * Subscribe to the fogbugz-formulator events of one session while mounted.
 *
 * - `turn-started`: the turn runs (its failure, if any, is cleared).
 * - `turn-finished`: the turn ended and SpecTree accepted it; the session,
 *   the resume list and the review are refetched.
 * - `turn-failed`: nothing was posted; the page shows the plain message and
 *   keeps the typed answer.
 * - `summary-ready`: an event-log summary finished; `onSummary` gets it.
 *
 * Events cross the app/page version boundary, so each is parsed with the
 * contract's schema and one the page cannot read is dropped. Turn outcomes
 * are passed to the title-bar Claude chip (`useClaudeRunOutcome`).
 */
export function useFogbugzFormulatorEvents(
  bridge: FogbugzFormulatorToolsetBridge | null,
  sessionId: string | null,
  options: { onSummary?: (name: string, sections: string) => void } = {},
): {
  turn: FogbugzFormulatorTurnState;
  markRunning: (mode: "opening" | "reply") => void;
  markFailed: (code: string, message: string) => void;
  clearFailure: () => void;
} {
  const queryClient = useQueryClient();
  const reportClaudeRun = useClaudeRunOutcome();
  const [turn, setTurn] = useState<FogbugzFormulatorTurnState>(IDLE);
  const onSummary = useRef(options.onSummary);
  useEffect(() => {
    onSummary.current = options.onSummary;
  }, [options.onSummary]);

  useEffect(() => {
    setTurn(IDLE);
  }, [sessionId]);

  useEffect(() => {
    if (!bridge || sessionId === null) return undefined;
    return bridge.onEvent((raw: unknown) => {
      const parsed = fogbugzFormulatorEventSchema.safeParse(raw);
      if (!parsed.success) return;
      const event = parsed.data;
      if (event.sessionId !== sessionId) return;
      switch (event.type) {
        case "turn-started": {
          const startedAt = Date.parse(event.startedAt);
          setTurn((current) => ({
            ...current,
            running: { mode: event.mode, startedAt: Number.isNaN(startedAt) ? Date.now() : startedAt },
            failure: null,
          }));
          return;
        }
        case "turn-finished":
          setTurn((current) => ({ running: null, failure: null, finishedCount: current.finishedCount + 1 }));
          reportClaudeRun({ ok: true });
          void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.session(sessionId) });
          void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.active() });
          void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.review(sessionId) });
          return;
        case "turn-failed":
          setTurn((current) => ({ ...current, running: null, failure: { code: event.code, message: event.message } }));
          reportClaudeRun({ ok: false, errorCode: event.code });
          return;
        case "summary-ready":
          onSummary.current?.(event.name, event.sections);
          return;
      }
    });
  }, [bridge, queryClient, reportClaudeRun, sessionId]);

  return {
    turn,
    markRunning: (mode) => {
      setTurn((current) => ({ ...current, running: { mode, startedAt: Date.now() }, failure: null }));
    },
    markFailed: (code, message) => {
      setTurn((current) => ({ ...current, running: null, failure: { code, message } }));
    },
    clearFailure: () => {
      setTurn((current) => ({ ...current, failure: null }));
    },
  };
}

// ---------------------------------------------------------------------------
// Kept on this computer: discarded sessions and reviewed summaries
// ---------------------------------------------------------------------------

/**
 * Sessions the person discarded on this computer. SpecTree has no delete
 * route for these sessions; a discarded one is hidden from the resume list
 * here and expires on its own (two hours idle).
 */
export const DISCARDED_SESSIONS_STORAGE_KEY = "spectree.toolbox.fogbugz-formulator.discarded";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the page works without it.
  }
}

/** The ids discarded on this computer. */
export function discardedSessionIds(): string[] {
  const value = readJson<unknown>(DISCARDED_SESSIONS_STORAGE_KEY, []);
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];
}

/** Hide `sessionId` from the resume list on this computer (the newest 50 are kept). */
export function discardSession(sessionId: string): void {
  const ids = discardedSessionIds().filter((id) => id !== sessionId);
  writeJson(DISCARDED_SESSIONS_STORAGE_KEY, [sessionId, ...ids].slice(0, 50));
}

/** Where the reviewed `.evtx` summaries of one session are kept until submit. */
export function summariesStorageKey(sessionId: string): string {
  return `spectree.toolbox.fogbugz-formulator.summaries.${sessionId}`;
}

/**
 * The summaries the person accepted for a session, by event-log file name.
 * They stay on this computer with the files (like the files, they are not on
 * SpecTree before submit), so a reload or an app restart keeps them.
 */
export function readAcceptedSummaries(sessionId: string): Record<string, string> {
  const value = readJson<unknown>(summariesStorageKey(sessionId), {});
  if (value === null || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [name, sections] of Object.entries(value as Record<string, unknown>)) {
    if (typeof sections === "string") out[name] = sections;
  }
  return out;
}

export function writeAcceptedSummaries(sessionId: string, summaries: Record<string, string>): void {
  writeJson(summariesStorageKey(sessionId), summaries);
}

export function clearAcceptedSummaries(sessionId: string): void {
  try {
    window.localStorage.removeItem(summariesStorageKey(sessionId));
  } catch {
    // Nothing to clear.
  }
}

/**
 * A plain attachment file name for the bridge: no path separators, no `..`,
 * no leading dot, at most 200 characters, and not one of `taken` (a pasted
 * screenshot is always "image.png"; the second becomes "image-2.png").
 * Pure; exported for tests.
 */
export function attachmentName(raw: string, taken: readonly string[], fallback = "screenshot.png"): string {
  let name = raw.replace(/[/\\\0]/g, "-").replace(/\.\.+/g, ".").replace(/^\.+/, "").trim();
  if (name === "") name = fallback;
  const dot = name.lastIndexOf(".");
  const stem = (dot > 0 ? name.slice(0, dot) : name).slice(0, 180);
  const ext = dot > 0 ? name.slice(dot).slice(0, 15) : "";
  let candidate = `${stem}${ext}`;
  const used = new Set(taken.map((n) => n.toLowerCase()));
  for (let n = 2; used.has(candidate.toLowerCase()); n += 1) candidate = `${stem}-${String(n)}${ext}`;
  return candidate;
}
